import React, { useState } from 'react';
import {
  Box,
  Button,
  Sheet,
  Typography,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Textarea,
  Input,
  Stack,
  Checkbox,
  Select,
  Option,
  Alert,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
} from '@mui/joy';
import { ResponsiveTable, type PaginationConfig, type ResponsiveColumn } from '../../common/ResponsiveTable';
import {
  Plus as AddIcon,
  PaperPlaneTilt as SendIcon,
  Envelope as EmailIcon,
  CheckCircle as CheckCircleIcon,
  XCircle as CancelIcon,
  Clock as ScheduleIcon,
  Trash as DeleteIcon,
  ArrowCounterClockwise as RestartIcon,
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReviewPage } from '../../../types/reviews';
import { api } from '../../../lib/api';
import { format } from 'date-fns';

interface Contact {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
  phone?: string;
  do_not_email?: boolean;
}

interface ReviewRequest {
  id: number;
  contact: number;
  contact_name: string;
  contact_email: string;
  platform: number;
  platform_name: string;
  status: string;
  failed_reason?: string;
  email_subject: string;
  sent_at?: string;
  opened_at?: string;
  clicked_at?: string;
  completed_at?: string;
  tracking_url: string;
  created_at: string;
  parent_request?: number;
  cadence_step?: number;
  cadence_total?: number;
  scheduled_for?: string;
}

interface ReviewPlatform {
  id: number;
  platform: string;
  account_name: string;
  is_active: boolean;
  review_url?: string;
}

export const ReviewRequests: React.FC = () => {
  const [activeTab, setActiveTab] = useState('in-queue');
  const [page, setPage] = useState(1);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<number[]>([]);
  const [selectedPlatform, setSelectedPlatform] = useState<number | null>(null);
  const [emailSubject, setEmailSubject] = useState("We'd love your feedback!");
  const [emailTemplate, setEmailTemplate] = useState('');
  const [intervalDays, setIntervalDays] = useState(3);
  const [startAt, setStartAt] = useState('');
  const [fromName, setFromName] = useState('');
  const [contactPage, setContactPage] = useState(1);
  const [cadenceCount, setCadenceCount] = useState(3);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirmRequest, setDeleteConfirmRequest] = useState<ReviewRequest | null>(null);
  const [restartConfirmRequest, setRestartConfirmRequest] = useState<ReviewRequest | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    color: 'success' as 'success' | 'danger'
  });
  const queryClient = useQueryClient();

  // Each group is filtered and paginated by the API before rows reach the table.
  const { data: requestPage, isLoading: requestsLoading, error: requestsError } = useQuery({
    queryKey: ['review-requests', activeTab, page],
    queryFn: async () => {
      const params = new URLSearchParams({ group: activeTab, page: String(page) });
      const response = await api.get<ReviewPage<ReviewRequest>>(`/api/marketing/review-requests/?${params}`);
      return response.data;
    },
  });
  const requests = requestPage?.results || [];
  const pagination: PaginationConfig = { page, pageSize: 20, totalCount: requestPage?.count || 0, onPageChange: setPage, itemNoun: 'requests' };
  React.useEffect(() => {
    if (requestPage && page > Math.max(1, Math.ceil(requestPage.count / 20))) setPage(Math.max(1, Math.ceil(requestPage.count / 20)));
  }, [requestPage, page]);

  // Fetch contacts
  const { data: contactResults, isLoading: contactsLoading } = useQuery({
    queryKey: ['contacts', searchQuery, contactPage],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(contactPage) });
      if (searchQuery) params.append('search', searchQuery);

      const response = await api.get<ReviewPage<Contact>>(`/api/crm/contacts/?${params}`);
      return response.data;
    },
  });

  const contacts = contactResults?.results || [];

  // Fetch platforms
  const { data: platforms = [] } = useQuery({
    queryKey: ['review-platforms-active'],
    queryFn: async () => {
      const response = await api.get<{ results: ReviewPlatform[] }>('/api/marketing/review-platforms/?is_active=true');
      return response.data.results || [];
    },
  });

  // Send bulk review requests
  const sendBulkRequests = useMutation({
    mutationFn: async (data: {
      contact_ids: number[];
      platform_id: number;
      email_subject: string;
      email_template: string;
      cadence_count: number;
      interval_days: number;
      scheduled_for?: string;
      from_name?: string;
    }) => {
      const response = await api.post('/api/marketing/review-requests/bulk-send/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-requests'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
      setShowRequestModal(false);
      setSelectedContacts([]);
      setEmailTemplate('');
    },
  });

  // Delete review request
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/marketing/review-requests/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review-requests'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
      setDeleteConfirmRequest(null);
      setSnackbar({
        open: true,
        message: 'Review request deleted successfully',
        color: 'success'
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail ||
                           error?.response?.data?.error ||
                           'Failed to delete review request';
      setSnackbar({
        open: true,
        message: errorMessage,
        color: 'danger'
      });
    },
  });

  // Restart review request cadence
  const restartMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<{ message?: string }>(`/api/marketing/review-requests/${id}/restart/`, {});
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['review-requests'] });
      queryClient.invalidateQueries({ queryKey: ['review-stats'] });
      setRestartConfirmRequest(null);
      setSnackbar({
        open: true,
        message: data?.message || 'Review cadence restarted',
        color: 'success'
      });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail ||
                           error?.response?.data?.error ||
                           'Failed to restart review request';
      setSnackbar({
        open: true,
        message: errorMessage,
        color: 'danger'
      });
    },
  });

  const defaultEmailTemplate = `Hi {contact_name},

Thank you for choosing {organization_name} for your recent project! We hope you're happy with the work we did.

We'd really appreciate it if you could take a moment to share your experience by leaving us a review. Your feedback helps us improve and helps other customers make informed decisions.

Click here to leave your review: {review_url}

Thank you for your time and for trusting us with your project!

Best regards,
The {organization_name} Team`;

  const campaignPayload = {
    contact_ids: selectedContacts, platform_id: selectedPlatform || 0, email_subject: emailSubject,
    email_template: emailTemplate || defaultEmailTemplate, cadence_count: cadenceCount, interval_days: intervalDays,
    ...(startAt ? { scheduled_for: new Date(startAt).toISOString() } : {}),
    ...(fromName.trim() ? { from_name: fromName.trim() } : {}),
  };
  const previewKey = JSON.stringify(campaignPayload);
  const previewCampaign = useMutation({
    mutationFn: async () => ({ key: previewKey, preview: (await api.post<import('../../../types/reviews').CampaignPreview>('/api/marketing/review-requests/preview/', campaignPayload)).data }),
  });
  const preview = previewCampaign.data?.key === previewKey ? previewCampaign.data.preview : undefined;
  const controlCampaign = useMutation({
    mutationFn: async (row: ReviewRequest) => api.post(`/api/marketing/review-requests/${row.id}/${row.status === 'paused' ? 'resume' : 'pause'}/`, {}),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['review-requests'] }); },
  });
  const handleSendRequests = () => {
    if (preview && !preview.recipients.some(r => r.status === 'blocked') && preview.recipients.some(r => r.status === 'ready')) sendBulkRequests.mutate(campaignPayload);
  };

  const toggleContactSelection = (contactId: number) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  const eligiblePageIds = contacts.filter(contact => contact.email && !contact.do_not_email).map(contact => contact.id);
  const pageSelected = eligiblePageIds.length > 0 && eligiblePageIds.every(id => selectedContacts.includes(id));
  const selectAllContacts = () => {
    setSelectedContacts(previous => pageSelected
      ? previous.filter(id => !eligiblePageIds.includes(id))
      : [...new Set([...previous, ...eligiblePageIds])]);
  };

  return (
    <Box>
      {/* Actions Bar */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          startDecorator={<AddIcon />}
          onClick={() => setShowRequestModal(true)}
        >
          Request Review
        </Button>
      </Box>

      {controlCampaign.isError && <Alert color="danger">Unable to change campaign status. Please refresh and try again.</Alert>}
      {requestsError && <Alert color="danger">Unable to load review requests. Please try again.</Alert>}
      {requestsLoading && <Typography level="body-sm">Loading requests…</Typography>}
      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(event, newValue) => { setActiveTab(newValue as string); setPage(1); }}
        sx={{ mb: 2 }}
      >
        <TabList
          sx={{
            flexWrap: 'wrap',
            '& > *': { flex: { xs: '1 1 45%', sm: '0 0 auto' } },
          }}
        >
          <Tab value="in-queue">In-Queue</Tab>
          <Tab value="active">Active</Tab>
          <Tab value="complete">Complete</Tab>
          <Tab value="failed">Failed</Tab>
          <Tab value="email-activity">Email Activity</Tab>
        </TabList>

        <TabPanel value="in-queue">
          <RequestsTable
            requests={requests} pagination={pagination} onControl={row => controlCampaign.mutate(row)}
            onDelete={setDeleteConfirmRequest}
            showScheduledFor
          />
        </TabPanel>

        <TabPanel value="active">
          <RequestsTable
            requests={requests} pagination={pagination} onControl={row => controlCampaign.mutate(row)}
            onDelete={setDeleteConfirmRequest}
            onRestart={setRestartConfirmRequest}
          />
        </TabPanel>

        <TabPanel value="complete">
          <RequestsTable
            requests={requests} pagination={pagination} onControl={row => controlCampaign.mutate(row)}
            onDelete={setDeleteConfirmRequest}
            onRestart={setRestartConfirmRequest}
          />
        </TabPanel>

        <TabPanel value="failed">
          <RequestsTable requests={requests} pagination={pagination} onControl={row => controlCampaign.mutate(row)} onRestart={setRestartConfirmRequest} />
        </TabPanel>
        <TabPanel value="email-activity">
          <EmailActivityTable requests={requests} pagination={pagination} />
        </TabPanel>
      </Tabs>

      {/* Request Review Modal */}
      <Modal
        open={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <ModalDialog sx={{ width: '90%', maxWidth: 800, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Request Review
          </Typography>

          <Stack spacing={3}>
            <Alert color="primary" variant="soft">
              Select contacts and a review platform to send review invitations via email.
            </Alert>

            {/* Platform Selection */}
            <FormControl required>
              <FormLabel>Review Platform</FormLabel>
              <Select
                value={selectedPlatform}
                onChange={(_, value) => setSelectedPlatform(value as number)}
                placeholder="Select a platform"
              >
                {platforms.map((platform) => (
                  <Option key={platform.id} value={platform.id}>
                    {platform.account_name} ({platform.platform})
                  </Option>
                ))}
              </Select>
            </FormControl>

            {/* Email Settings */}
            <FormControl required>
              <FormLabel>Email Subject</FormLabel>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Enter email subject"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Email Template (Optional)</FormLabel>
              <Textarea
                value={emailTemplate}
                onChange={(e) => setEmailTemplate(e.target.value)}
                placeholder={defaultEmailTemplate}
                minRows={6}
                maxRows={12}
              />
              <Typography level="body-xs" sx={{ mt: 1, color: 'text.secondary' }}>
                Available variables: {'{contact_name}'}, {'{organization_name}'}, {'{review_url}'}
              </Typography>
            </FormControl>

            {/* Cadence Settings */}
            <FormControl>
              <FormLabel>Number of Emails</FormLabel>
              <Select
                value={cadenceCount}
                onChange={(_, value) => setCadenceCount(value as number)}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <Option key={n} value={n}>
                    {n} {n === 1 ? 'email (no follow-ups)' : `emails (${n - 1} follow-up${n > 2 ? 's' : ''})`}
                  </Option>
                ))}
              </Select>
              <Typography level="body-xs" sx={{ mt: 1, color: 'text.secondary' }}>
                Follow-ups use the start time and spacing below. Times are displayed in your browser’s local time.
                Sending stops automatically when a contact clicks the review link.
              </Typography>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <FormControl><FormLabel>Sender name</FormLabel><Input value={fromName} onChange={e => setFromName(e.target.value)} placeholder="Business name" /></FormControl>
              <FormControl><FormLabel>Start time (blank = now)</FormLabel><Input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} /></FormControl>
              <FormControl><FormLabel>Days between emails</FormLabel><Input type="number" value={intervalDays} slotProps={{ input: { min: 1, max: 30 } }} onChange={e => setIntervalDays(Number(e.target.value))} /></FormControl>
            </Stack>
            {/* Contact Selection */}
            <Box>
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormLabel sx={{ mb: 0 }}>Select Recipients</FormLabel>
                <Input
                  size="sm"
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setContactPage(1); }}
                  sx={{ flex: 1, maxWidth: 300 }}
                />
                <Button
                  size="sm"
                  variant="plain"
                  onClick={selectAllContacts}
                >
                  {pageSelected ? 'Clear page' : 'Select page'}
                </Button>
              </Box>

              <ResponsiveTable<Contact> rows={contacts} getRowKey={row => row.id}
                columns={[
                  { key: 'name', header: 'Name', mobileVariant: 'header', render: row => row.full_name },
                  { key: 'email', header: 'Email', render: row => row.email || 'Missing email' },
                  { key: 'eligibility', header: 'Eligibility', render: row => row.do_not_email ? 'Email opted out' : row.email ? 'Check in preview' : 'Email required' },
                ]}
                rowActions={row => <Checkbox aria-label={`Select ${row.full_name}`} disabled={!row.email || row.do_not_email}
                  checked={selectedContacts.includes(row.id)} onChange={() => toggleContactSelection(row.id)} />}
                pagination={{ page: contactPage, pageSize: 20, totalCount: contactResults?.count || 0, onPageChange: setContactPage, itemNoun: 'contacts' }}
                emptyState={<Typography>{contactsLoading ? 'Loading contacts…' : 'No contacts found'}</Typography>} />

              {selectedContacts.length > 0 && (
                <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
                  {selectedContacts.length} contact(s) selected
                </Typography>
              )}
            </Box>

            {(sendBulkRequests.isError || previewCampaign.isError) && <Alert color="danger">{String(((sendBulkRequests.error || previewCampaign.error) as { response?: { data?: { error?: string } } } | null)?.response?.data?.error || 'Unable to validate or queue this campaign. Check recipients, platform and schedule.')}</Alert>}
            <Button variant="outlined" loading={previewCampaign.isPending} disabled={!selectedPlatform || !selectedContacts.length || !emailSubject.trim()}
              onClick={() => previewCampaign.mutate()}>Preview & validate campaign</Button>
            {preview && <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
              <Typography level="h4">{preview.organization_name}</Typography>
              <Typography level="body-sm">From: {preview.sender}</Typography>
              <Typography level="title-md">{preview.subject}</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap', my: 2 }}>{preview.recipients[0]?.body}</Typography>
              <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>Destination: {preview.destination}</Typography>
              <Typography level="body-sm">Schedule: {preview.schedule.map(date => new Date(date).toLocaleString()).join(' · ')}</Typography>
              <ResponsiveTable rows={preview.recipients} getRowKey={row => row.id} columns={[
                { key: 'name', header: 'Recipient', mobileVariant: 'header', render: row => row.name || row.email },
                { key: 'status', header: 'Eligibility', render: row => row.reason || row.status },
              ]} />
              <Typography level="body-xs">Recent duplicates are skipped. Opted-out or invalid recipients must be removed before sending. The tracked link is created when queued.</Typography>
            </Sheet>}
            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="plain" onClick={() => setShowRequestModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSendRequests}
                loading={sendBulkRequests.isPending}
                disabled={!preview || preview.recipients.some(r => r.status === 'blocked') || !preview.recipients.some(r => r.status === 'ready')}
                startDecorator={<SendIcon />}
              >
                Queue validated campaign
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteConfirmRequest}
        onClose={() => setDeleteConfirmRequest(null)}
      >
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          aria-labelledby="delete-confirm-title"
        >
          <DialogTitle id="delete-confirm-title">
            <DeleteIcon style={{ marginRight: 8 }} />
            Delete Review Request
          </DialogTitle>
          <DialogContent>
            Are you sure you want to delete this review request for{' '}
            <strong>{deleteConfirmRequest?.contact_name}</strong>?
            <br />
            <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteConfirmRequest(null)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => deleteConfirmRequest && deleteMutation.mutate(deleteConfirmRequest.id)}
              loading={deleteMutation.isPending}
            >
              Delete Request
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Restart Confirmation Modal */}
      <Modal
        open={!!restartConfirmRequest}
        onClose={() => setRestartConfirmRequest(null)}
      >
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          aria-labelledby="restart-confirm-title"
        >
          <DialogTitle id="restart-confirm-title">
            <RestartIcon style={{ marginRight: 8 }} />
            Restart Review Cadence
          </DialogTitle>
          <DialogContent>
            Restart the review request cadence for{' '}
            <strong>{restartConfirmRequest?.contact_name}</strong>?
            <br />
            <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
              This will create a new set of {restartConfirmRequest?.cadence_total || 3} emails using the original settings.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setRestartConfirmRequest(null)}
              disabled={restartMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => restartConfirmRequest && restartMutation.mutate(restartConfirmRequest.id)}
              loading={restartMutation.isPending}
              startDecorator={<RestartIcon />}
            >
              Restart
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        color={snackbar.color}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};

// Helper Components
const getStatusColor = (status: string) => {
  const colors: Record<string, any> = {
    pending: 'neutral',
    scheduled: 'neutral',
    sent: 'primary',
    opened: 'info',
    clicked: 'warning',
    completed: 'success',
    cancelled: 'danger',
    expired: 'danger',
  };
  return colors[status] || 'neutral';
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending':
    case 'scheduled':
      return <ScheduleIcon size={16} />;
    case 'sent':
      return <SendIcon size={16} />;
    case 'opened':
      return <EmailIcon size={16} />;
    case 'completed':
      return <CheckCircleIcon size={16} />;
    case 'cancelled':
    case 'expired':
      return <CancelIcon size={16} />;
    default:
      return null;
  }
};

const RequestsTable: React.FC<{
  requests: ReviewRequest[];
  pagination: PaginationConfig;
  onControl?: (request: ReviewRequest) => void;
  onDelete?: (request: ReviewRequest) => void;
  onRestart?: (request: ReviewRequest) => void;
  showScheduledFor?: boolean;
}> = ({ requests, pagination, onControl, onDelete, onRestart, showScheduledFor }) => {
  const columns: ResponsiveColumn<ReviewRequest>[] = [
    {
      key: 'contact',
      sortValue: (row) => row.contact_name,
      header: 'Contact',
      mobileVariant: 'header',
      render: (r) => <Typography level="title-sm">{r.contact_name}</Typography>,
    },
    {
      key: 'email',
      sortValue: (row) => row.contact_email,
      header: 'Email',
      render: (r) => (
        <Typography level="body-sm" sx={{ wordBreak: 'break-all' }}>
          {r.contact_email}
        </Typography>
      ),
    },
    {
      key: 'platform',
      sortValue: (row) => row.platform_name,
      header: 'Platform',
      render: (r) => (
        <Chip size="sm" variant="soft">
          {r.platform_name}
        </Chip>
      ),
    },
    {
      key: 'step',
      sortValue: (row) => row.cadence_step,
      header: 'Step',
      render: (r) =>
        r.cadence_total && r.cadence_total > 1 ? (
          <Typography level="body-sm">
            {r.cadence_step}/{r.cadence_total}
          </Typography>
        ) : (
          <Typography level="body-sm">-</Typography>
        ),
    },
    {
      key: 'status',
      sortValue: (row) => row.status,
      header: 'Status',
      render: (r) => (
        <Chip
          size="sm"
          color={getStatusColor(r.status)}
          startDecorator={getStatusIcon(r.status)}
        >
          {r.status}
          {r.failed_reason ? `: ${r.failed_reason}` : ''}
        </Chip>
      ),
    },
    {
      key: 'when',
      sortValue: (row) => row.sent_at ?? row.scheduled_for,
      header: showScheduledFor ? 'Scheduled' : 'Sent',
      render: (r) => (
        <Typography level="body-sm">
          {showScheduledFor && r.scheduled_for
            ? format(new Date(r.scheduled_for), 'MMM d, yyyy h:mm a')
            : r.sent_at
              ? format(new Date(r.sent_at), 'MMM d, yyyy')
              : '-'}
        </Typography>
      ),
    },
  ];

  const renderActions = (request: ReviewRequest) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
            {onControl && ['pending', 'scheduled', 'paused', 'sent', 'opened'].includes(request.status) && <Button size="sm" variant="plain" onClick={() => onControl(request)}>{request.status === 'paused' ? 'Resume' : 'Pause'}</Button>}
      {onRestart && !((['completed', 'cancelled'].includes(request.status)) && request.clicked_at) && (
        <IconButton
          size="sm"
          color="primary"
          variant="plain"
          onClick={() => onRestart(request)}
          aria-label="Restart review cadence"
        >
          <RestartIcon />
        </IconButton>
      )}
      {onDelete && (
        <IconButton
          size="sm"
          color="danger"
          variant="plain"
          onClick={() => onDelete(request)}
          aria-label="Delete review request"
        >
          <DeleteIcon />
        </IconButton>
      )}
    </Box>
  );

  return (
    <ResponsiveTable<ReviewRequest>
      rows={requests}
      pagination={pagination}
      getRowKey={(r) => r.id}
      columns={columns}
      rowActions={onControl || onDelete || onRestart ? renderActions : undefined}
      actionsWidth="90px"
      emptyState={
        <Box sx={{ p: 4, textAlign: 'center' }}>

          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            No review requests found
          </Typography>
        </Box>
      }
    />
  );
};

const EmailActivityTable: React.FC<{ requests: ReviewRequest[]; pagination: PaginationConfig }> = ({ requests, pagination }) => {
  const columns: ResponsiveColumn<ReviewRequest>[] = [
    {
      key: 'contact',
      sortValue: (row) => row.contact_name,
      header: 'Contact',
      mobileVariant: 'header',
      render: (r) => <Typography level="title-sm">{r.contact_name}</Typography>,
    },
    {
      key: 'subject',
      sortValue: (row) => row.email_subject,
      header: 'Subject',
      mobileVariant: 'body',
      render: (r) => <Typography level="body-sm">{r.email_subject}</Typography>,
    },
    {
      key: 'step',
      sortValue: (row) => row.cadence_step,
      header: 'Step',
      render: (r) =>
        r.cadence_total && r.cadence_total > 1 ? (
          <Typography level="body-sm">
            {r.cadence_step}/{r.cadence_total}
          </Typography>
        ) : (
          <Typography level="body-sm">-</Typography>
        ),
    },
    {
      key: 'sent',
      sortValue: (row) => row.sent_at ? new Date(row.sent_at) : null,
      header: 'Sent',
      render: (r) =>
        r.sent_at ? (
          <Chip size="sm" color="success" variant="soft">
            {format(new Date(r.sent_at), 'MMM d')}
          </Chip>
        ) : (
          <Typography level="body-sm">-</Typography>
        ),
    },
    {
      key: 'opened',
      sortValue: (row) => row.opened_at ? new Date(row.opened_at) : null,
      header: 'Opened',
      render: (r) =>
        r.opened_at ? (
          <Chip size="sm" color="primary" variant="soft">
            {format(new Date(r.opened_at), 'MMM d')}
          </Chip>
        ) : (
          <Typography level="body-sm">-</Typography>
        ),
    },
    {
      key: 'clicked',
      sortValue: (row) => row.clicked_at ? new Date(row.clicked_at) : null,
      header: 'Clicked',
      render: (r) =>
        r.clicked_at ? (
          <Chip size="sm" color="warning" variant="soft">
            {format(new Date(r.clicked_at), 'MMM d')}
          </Chip>
        ) : (
          <Typography level="body-sm">-</Typography>
        ),
    },
    {
      key: 'completed',
      sortValue: (row) => row.completed_at ? new Date(row.completed_at) : null,
      header: 'Completed',
      render: (r) =>
        r.completed_at ? (
          <Chip size="sm" color="success">
            {format(new Date(r.completed_at), 'MMM d')}
          </Chip>
        ) : (
          <Typography level="body-sm">-</Typography>
        ),
    },
  ];

  return (
    <ResponsiveTable<ReviewRequest>
      rows={requests}
      pagination={pagination}
      getRowKey={(r) => r.id}
      columns={columns}
      emptyState={
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            No email activity found
          </Typography>
        </Box>
      }
    />
  );
};
